import type { ProductInfo } from "@expert/contentful";
import type { Partner } from "@expert/shared-types";

import { Box, Card, Flex, Group, Image, Stack, Text } from "@mantine/core";
import clsx from "clsx";

import { DeclinedIcon } from "../../../../../icons/DeclinedIcon";
import { EnrolledIcon } from "../../../../../icons/EnrolledIcon";
import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { useCallDetailsStore, useProductOfferStore } from "../../../../../stores";
import { SaleProgress } from "../../../../../types";
import { isRecommendedOverride } from "../OffersView.helpers";
import { PlanBadge } from "./PlanBadge";
import { determineCardStyle } from "./PlanCard.helpers";
import classes from "./PlanCard.module.css";

export interface FullPlanCardProps {
    saleProgress: SaleProgress;
    partner: Partner;
    product: ProductInfo | undefined;
    onClick: () => void;
    isSelected?: boolean;
    showEligibleTag?: boolean;
    position?: number;
}

export const FullPlanCard = ({
    saleProgress,
    partner,
    product,
    onClick,
    isSelected = false,
    showEligibleTag = false,
    position,
}: FullPlanCardProps) => {
    const { sessionId, features } = useSalesContext();
    const isSalesQuestionnaireEnabled = features.isSalesQuestionnaireEnabled;

    if (!product) return null;

    const products = useProductOfferStore.getState().getProducts(sessionId);
    const client = useCallDetailsStore.getState().callDetails.CLIENT;
    const overrideResult = isRecommendedOverride(products, client, product.productSku);
    const baseIsRecommended = (position === 0 && isSalesQuestionnaireEnabled) || product.isRecommended;
    const isProductRecommended = overrideResult !== undefined ? overrideResult : baseIsRecommended;

    const isClickable = saleProgress === SaleProgress.InProgress && typeof onClick === "function";
    const handleClick = isClickable ? onClick : undefined;
    const { displayName, tagline, logo, partnerLogo } = product;
    const uniqueId = `${displayName}-card`;
    const isEnrolledOrDeclined = ([SaleProgress.Success, SaleProgress.Declined] as SaleProgress[]).includes(
        saleProgress,
    );
    const isInProgressOrFailure = ([SaleProgress.InProgress, SaleProgress.Failure] as SaleProgress[]).includes(
        saleProgress,
    );

    return (
        <Card
            data-testid={uniqueId}
            id={uniqueId}
            key={uniqueId}
            data-sales-progress={saleProgress}
            onClick={handleClick}
            classNames={{
                root: clsx(
                    classes.fullPlanCard,
                    isSelected ? classes.planCardSelected : classes.cardContainer,
                    determineCardStyle(saleProgress),
                ),
            }}
            radius="md"
            aria-label={uniqueId}
            h="auto"
            mih={86}
            p="sm"
        >
            <Group wrap="nowrap" gap="xs">
                <Image
                    w="auto"
                    classNames={{ root: classes.planNameRoot }}
                    data-testid={`${partner}-logo`}
                    alt={`${partner}-logo`}
                    src={partnerLogo}
                    mah={16}
                />
                <Text size="xl">|</Text>
                <Text data-testid={`displayName-${displayName}`} flex={1} size="sm">
                    {displayName}
                </Text>
            </Group>

            <Text data-testid="tagline" flex={1} size="sm" mt="xxs">
                {tagline}
            </Text>

            <Group wrap="nowrap" justify="space-between" mt="xs" gap={0} align="flex-end">
                <Stack gap="xxs" flex={1}>
                    {saleProgress === SaleProgress.InProgress && showEligibleTag && (
                        <PlanBadge text="Eligible" testId={`${displayName}-eligible-badge`} />
                    )}

                    {saleProgress === SaleProgress.InProgress && isProductRecommended && !showEligibleTag && (
                        <PlanBadge text="Recommended" testId={`${displayName}-recommended-badge`} />
                    )}

                    {!isInProgressOrFailure ? (
                        <Flex gap="xxs" align="center">
                            {saleProgress === SaleProgress.Success ? (
                                <EnrolledIcon data-testid="enrolled-icon" height="15px" width="15px" />
                            ) : (
                                <DeclinedIcon data-testid="declined-icon" height="15px" width="15px" />
                            )}
                            <Text size="xs">{saleProgress === SaleProgress.Success ? "Enrolled" : "Declined"}</Text>
                        </Flex>
                    ) : null}

                    {!isEnrolledOrDeclined && isSelected && (
                        <Flex>
                            <PlanBadge className={classes.selectedBadge} text="Started" />
                        </Flex>
                    )}
                </Stack>

                {logo && (
                    <Box
                        style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "flex-end",
                            height: "100%",
                            minWidth: 0,
                        }}
                    >
                        <Image
                            src={logo}
                            mah={50}
                            maw={50}
                            fit="contain"
                            data-testid={`${displayName}-image`}
                            alt={`${displayName} product image`}
                            style={{ marginBottom: -8 }}
                        />
                    </Box>
                )}
            </Group>
        </Card>
    );
};
